import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Grid, Button, Alert, CircularProgress, InputAdornment, IconButton, TextField } from '@mui/material';
import { FileDownloadOutlined, Clear as ClearIcon, ClearAll as ClearAllIcon } from '@mui/icons-material';
import { TableBody, TableContainer } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid } from 'date-fns';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../uiComponents/MuiTable/StyledTable';
import MuiTableHeader from '../../uiComponents/MuiTable/MuiTableHeader';
import MuiTablePagination from '../../uiComponents/MuiTable/MuiTablePagination';
import APIUtils from '../../utils/APIUtils';
import { DateUtils } from '../../utils/DateUtils';
import { exportToCSV } from '../../utils/ExportToCSV';

// SortBy values sent to the API must equal the record field names.
const headCells = [
  { label: 'Date', id: 'updatedDate', numeric: false, disablePadding: false },
  { label: 'Updated By', id: 'updatedBy', numeric: false, disablePadding: false },
  { label: 'Area', id: 'area', numeric: false, disablePadding: false },
  { label: 'Setting', id: 'setting', numeric: false, disablePadding: false },
  { label: 'Action', id: 'action', numeric: false, disablePadding: false },
  { label: 'Details', id: 'description', numeric: false, disablePadding: false, type: 'noSort' },
];

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];
const DEFAULT_ROWS_PER_PAGE = 10;
const MIN_DATE = new Date(2020, 0, 1);

// Match the app's focus highlight color (not MUI default blue), preserving the error state.
const DATE_FIELD_FOCUS_SX = {
  '& label.Mui-focused:not(.Mui-error)': { color: 'var(--accent-primary-color)' },
  '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--accent-primary-color)',
  },
};

// Combine the user's name and email/ID into one column, e.g. "Jane Doe (jane@cdc.gov)".
const formatUpdatedBy = (row) => {
  const name = row.updatedBy ?? '';
  return row.email ? `${name} (${row.email.toLowerCase()})` : name;
};

// Display the name on the first line and the parenthesized email on its own line.
const renderUpdatedBy = (row) => {
  const name = row.updatedBy ?? '';
  if (!row.email) return name;
  return (
    <>
      {name}
      <br />
      {`(${row.email.toLowerCase()})`}
    </>
  );
};

// API date params expect a plain calendar date (yyyy-MM-dd).
const toApiDate = (date) => (date && isValid(date) ? format(date, 'yyyy-MM-dd') : undefined);

function AuditTab({ programId }) {
  const [records, setRecords] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('updatedDate');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const maxDate = new Date();

  // Only pass valid dates to the API; an in-progress/invalid entry is ignored.
  const validFrom = fromDate && isValid(fromDate) && fromDate >= MIN_DATE && fromDate <= maxDate ? fromDate : null;
  const validTo = toDate && isValid(toDate) && toDate >= MIN_DATE && toDate <= maxDate ? toDate : null;
  const rangeValid = !(validFrom && validTo) || validFrom <= validTo;

  const buildParams = useCallback((overrides = {}) => ({
    Page: page + 1,
    PageSize: rowsPerPage,
    SortBy: orderBy,
    SortOrder: order,
    UpdatedDateFrom: toApiDate(validFrom),
    UpdatedDateTo: toApiDate(validTo),
    ...overrides,
  }), [page, rowsPerPage, orderBy, order, validFrom, validTo]);

  useEffect(() => {
    if (!programId || !rangeValid) return undefined;
    let cancelled = false;
    const fetchHistory = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await APIUtils.getProgramSettingHistory(programId, buildParams());
        if (cancelled) return;
        setRecords(response?.records || []);
        setTotalRecords(response?.totalRecords || 0);
      } catch (err) {
        if (cancelled) return;
        console.error('Error getting program setting history: ' + err);
        setError('Unable to load the audit log. Please try again.');
        setRecords([]);
        setTotalRecords(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchHistory();
    return () => { cancelled = true; };
  }, [programId, rangeValid, buildParams]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFromDateChange = (value) => {
    setFromDate(value);
    setPage(0);
  };

  const handleToDateChange = (value) => {
    setToDate(value);
    setPage(0);
  };

  const clearDateFilters = () => {
    setFromDate(null);
    setToDate(null);
    setPage(0);
  };

  // Export the full filtered/sorted result set (all pages), not just the page in view.
  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const response = await APIUtils.getProgramSettingHistory(
        programId,
        buildParams({ Page: 1, PageSize: totalRecords || 1 })
      );
      const allRecords = (response?.records || []).map((r) => ({
        updatedDate: DateUtils.FormatLocalDate(r.updatedDate),
        updatedBy: formatUpdatedBy(r),
        area: r.area ?? '',
        setting: r.setting ?? '',
        action: r.action ?? '',
        description: r.description ?? '',
      }));
      const exportHeaders = [
        { label: 'Date', key: 'updatedDate' },
        { label: 'Updated By', key: 'updatedBy' },
        { label: 'Area', key: 'area' },
        { label: 'Setting', key: 'setting' },
        { label: 'Action', key: 'action' },
        { label: 'Details', key: 'description' },
      ];
      exportToCSV(allRecords, `ProgramAudit-${programId}.csv`, exportHeaders);
    } catch (err) {
      console.error('Error exporting program setting history: ' + err);
      setError('Unable to export the audit log. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const filtersActive = Boolean(validFrom || validTo);
  // Distinguish "no history at all" from "filters excluded everything" (AC10).
  const emptyMessage = filtersActive
    ? 'No records match the selected date range.'
    : 'No changes have been recorded yet.';

  return (
    <Box className="psTabContent">
      <Typography variant="h6" component="h2" className="psTabTitle">Audit</Typography>

      <Grid container spacing={2} alignItems="center" sx={{ mt: 1, mb: 2 }}>
        <Grid item>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Updated From"
              value={fromDate}
              onChange={handleFromDateChange}
              minDate={MIN_DATE}
              maxDate={validTo || maxDate}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  sx={DATE_FIELD_FOCUS_SX}
                  error={Boolean(fromDate) && !rangeValid}
                  helperText={Boolean(fromDate) && !rangeValid ? 'From date must be before To date.' : ' '}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {fromDate && (
                          <InputAdornment position="end">
                            <IconButton aria-label="clear from date" size="small" edge="end" onClick={() => handleFromDateChange(null)}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )}
                        {params.InputProps?.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Updated To"
              value={toDate}
              onChange={handleToDateChange}
              minDate={validFrom || MIN_DATE}
              maxDate={maxDate}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  sx={DATE_FIELD_FOCUS_SX}
                  helperText={' '}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {toDate && (
                          <InputAdornment position="end">
                            <IconButton aria-label="clear to date" size="small" edge="end" onClick={() => handleToDateChange(null)}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )}
                        {params.InputProps?.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            onClick={clearDateFilters}
            disabled={!fromDate && !toDate}
            startIcon={<ClearAllIcon className="startIconLarger" />}
            aria-label="Clear table filters"
            sx={{ mb: '23px', whiteSpace: 'nowrap' }}
          >
            Clear Filters
          </Button>
        </Grid>
        <Grid item sx={{ ml: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadOutlined />}
            onClick={handleExport}
            disabled={exporting || totalRecords === 0}
            aria-label="Export audit log as CSV"
            sx={{ mb: '23px' }}
          >
            Export
          </Button>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <StyledPaper>
        <TableContainer sx={{ minHeight: 360, position: 'relative' }}>
          <StyledTable size="small" aria-label="Audit log">
            <MuiTableHeader
              headCells={headCells}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {records.length ? (
                records.map((row) => (
                  <StyledTableRow tabIndex={-1} key={row.id}>
                    <StyledTableCell>{DateUtils.FormatLocalDate(row.updatedDate)}</StyledTableCell>
                    <StyledTableCell sx={{ minWidth: 180, wordBreak: 'break-word' }}>{renderUpdatedBy(row)}</StyledTableCell>
                    <StyledTableCell>{row.area}</StyledTableCell>
                    <StyledTableCell>{row.setting}</StyledTableCell>
                    <StyledTableCell>{row.action}</StyledTableCell>
                    <StyledTableCell sx={{ minWidth: 300, wordBreak: 'break-word' }}>{row.description}</StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={headCells.length} style={{ textAlign: 'center', height: 300, borderBottom: 'none', color: 'rgba(0, 0, 0, 0.6)' }}>
                    {loading ? (
                      <CircularProgress aria-label="Loading audit records" size="4rem" />
                    ) : (
                      emptyMessage
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
          {/* Keep the prior rows mounted during server-side reloads so header column widths and table height stay put. */}
          {loading && records.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                zIndex: 1,
              }}
            >
              {/* Spinner is fixed to the viewport so it stays visible no matter where the tall table is scrolled. */}
              <CircularProgress
                aria-label="Loading audit records"
                size="4rem"
                sx={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
              />
            </Box>
          )}
        </TableContainer>
        <MuiTablePagination
          rowsPerPage={rowsPerPage}
          page={page}
          count={totalRecords}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      </StyledPaper>
    </Box>
  );
}

AuditTab.propTypes = {
  programId: PropTypes.string.isRequired,
};

export default AuditTab;
